import { FC } from "react"
import { LogSchedule } from "@/api/LogsService"
import {
   formatTime,
   formatTimeInterval,
   fromApiTime,
   uses12HourClock,
} from "@/pages/Settings/OpenCellSchedules/utils"
import { OpenCellScheduleEvent } from "@/types/logs"
import { formatWeekdays, fromApiWeekdays } from "@/utils/logs"

interface OpenCellScheduleDetailsProps {
   event: OpenCellScheduleEvent
   schedule: LogSchedule
   previousSchedule?: LogSchedule | null
}

interface WindowRow {
   interval: string
   weekdays: string
}

interface WindowChange {
   previous: WindowRow
   current: WindowRow
}

const NO_WINDOW: WindowRow = { interval: "none", weekdays: "none" }

const toWindowRows = ({ windows }: LogSchedule): WindowRow[] =>
   windows.map(({ startTime, endTime, weekdays }) => ({
      interval: formatTimeInterval(
         fromApiTime(startTime),
         fromApiTime(endTime),
         uses12HourClock()
      ),
      weekdays: formatWeekdays(fromApiWeekdays(weekdays)),
   }))

const findByInterval = (rows: WindowRow[], row: WindowRow) =>
   rows.find((other) => other.interval === row.interval)

const getWindowChanges = (
   previousRows: WindowRow[],
   rows: WindowRow[]
): WindowChange[] => {
   const weekdayChanges = rows.flatMap((current) => {
      const previous = findByInterval(previousRows, current)
      const hasNewWeekdays = previous && previous.weekdays !== current.weekdays

      return hasNewWeekdays ? [{ previous, current }] : []
   })
   const removed = previousRows.filter((row) => !findByInterval(rows, row))
   const added = rows.filter((row) => !findByInterval(previousRows, row))
   const intervalChanges = Array.from(
      { length: Math.max(removed.length, added.length) },
      (_, index) => ({
         previous: removed[index] ?? NO_WINDOW,
         current: added[index] ?? NO_WINDOW,
      })
   )

   return [...weekdayChanges, ...intervalChanges]
}

interface ScheduleRowProps {
   title: string
   value: string
   previousValue?: string
}

const ScheduleRow: FC<ScheduleRowProps> = ({ title, value, previousValue }) => (
   <div>
      <div className="log-card__title">{title}</div>
      <div>
         {previousValue !== undefined && previousValue !== value && (
            <>
               <span className="log-card__change-old-value">
                  {previousValue}
               </span>
               <span className="log-card__change-arrow">{" > "}</span>
            </>
         )}
         <span>{value}</span>
      </div>
   </div>
)

const WindowRows: FC<{ current: WindowRow; previous?: WindowRow }> = ({
   current,
   previous,
}) => (
   <>
      <ScheduleRow
         title="Time Interval"
         value={current.interval}
         previousValue={previous?.interval}
      />
      <ScheduleRow
         title="Weekdays"
         value={current.weekdays}
         previousValue={previous?.weekdays}
      />
   </>
)

const ScheduleSummary: FC<{ schedule: LogSchedule }> = ({ schedule }) => (
   <div className="log-card__schedule">
      <ScheduleRow
         title="Level"
         value={schedule.level.name}
      />
      {toWindowRows(schedule).map((row, index) => (
         <WindowRows
            key={index}
            current={row}
         />
      ))}
   </div>
)

const ScheduleChanges: FC<{
   previousSchedule: LogSchedule
   schedule: LogSchedule
}> = ({ previousSchedule, schedule }) => {
   const changes = getWindowChanges(
      toWindowRows(previousSchedule),
      toWindowRows(schedule)
   )

   return (
      <div className="log-card__schedule">
         <ScheduleRow
            title="Level"
            value={schedule.level.name}
            previousValue={previousSchedule.level.name}
         />
         {changes.map(({ previous, current }, index) => (
            <WindowRows
               key={index}
               current={current}
               previous={previous}
            />
         ))}
      </div>
   )
}

const OpenCellScheduleDetails: FC<OpenCellScheduleDetailsProps> = ({
   event,
   schedule,
   previousSchedule,
}) => {
   if (event === OpenCellScheduleEvent.STARTED) {
      const [openedWindow] = schedule.windows
      if (!openedWindow) return null

      const endTime = formatTime(
         fromApiTime(openedWindow.endTime),
         uses12HourClock()
      )
      return (
         <div className="log-card__schedule">
            {`No Vitals / Empty Cell alarm is deactivated until ${endTime}`}
         </div>
      )
   }

   if (event === OpenCellScheduleEvent.ENDED)
      return (
         <div className="log-card__schedule">
            No Vitals / Empty Cell alarm is active again
         </div>
      )

   if (event === OpenCellScheduleEvent.UPDATED && previousSchedule)
      return (
         <ScheduleChanges
            previousSchedule={previousSchedule}
            schedule={schedule}
         />
      )

   return <ScheduleSummary schedule={schedule} />
}

export default OpenCellScheduleDetails
